import {
  EDITOR_MODE,
  useBlocksConfig,
  useEditorStateSelector
} from "@pathwright/blocks-engine"
import { Content } from "@pathwright/ui/src/components/content/Content"
import { quillContentCSS } from "@pathwright/ui/src/components/quill/ui"
import { useKatex } from "@pathwright/utils/src/katex/useKatex"
import { resolveGenericLinksInHtml } from "@pathwright/utils/src/url/generic-link"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import styled from "styled-components"
import { getGenericLinkContextFrom } from "../utils/urls/genericLinks"
import { interpolateVariables } from "../utils/variables"

export const BlockTextWrap = styled.div`
  ${quillContentCSS}

  overflow-y: hidden;
  /* Allow side-scrolling for elements like tables */
  overflow-x: auto;

  /* Disable side-scrolling for specific element types */
  & > p > strong {
    overflow-x: hidden;
  }

  @media screen and (min-width: 900px) {
    & > p.ql-align-center {
      // Solves scrollbar issues in some Windows browsers
      overflow: hidden;
    }
  }

  .code-block {
    padding: 16px;
    display: block;
    white-space: pre-wrap !important;

    & > code {
      padding: 0;
      /* line-height: 1.65; */
    }
  }
`

const BlockText = (props) => {
  const {
    html,
    text,
    templateContext: propsTemplateContext,
    style,
    className
  } = props

  const { templateVariables: configTemplateContext } = useBlocksConfig()
  const isEditMode = useEditorStateSelector(
    (state) => !!state?.mode && state.mode === EDITOR_MODE.EDIT
  )

  const templateContext = propsTemplateContext || configTemplateContext

  let content = html ? html : text || ""

  if (templateContext) {
    try {
      content = interpolateVariables(content, templateContext)
    } catch (error) {
      console.error("Error parsing template: ", error.toString())
    }
  }

  // Resolving generic links here (rather than on click) means the anchor carries
  // a real cohort URL, so opening in a new tab, copying the link, etc. all work.
  if (html) {
    content = resolveGenericLinksInHtml(
      content,
      getGenericLinkContextFrom(templateContext)
    )
  }

  const textRef = useRef(null)

  useKatex(textRef, { enabled: !isEditMode, contentKey: content })

  useEffect(() => {
    const el = textRef.current
    if (!el) return

    const checkboxes = el.querySelectorAll("ul[data-checked] li")
    checkboxes.forEach((checkbox) => {
      checkbox.setAttribute(
        "aria-checked",
        checkbox.parentElement.getAttribute("data-checked")
      )
      checkbox.setAttribute("role", "checkbox")
      checkbox.setAttribute("tabindex", "0")
    })
  }, [content])

  if (html) {
    return (
      <Content.Text>
        <BlockTextWrap
          ref={textRef}
          className={classNames("BlockText", className)}
          dangerouslySetInnerHTML={{ __html: content }}
          style={style}
        />
      </Content.Text>
    )
  }

  return <div className="BlockText">{content}</div>
}

BlockText.propTypes = {
  html: PropTypes.string,
  text: PropTypes.string,
  templateContext: PropTypes.object,
  style: PropTypes.object,
  className: PropTypes.string
}

export default BlockText
